// src/utils/adjusterHome.js
//
// Números del inicio del ajustador (AdjusterHome.jsx), a partir de los
// reclamos y cheques que ese usuario ya puede leer (services/directoryData.js).
// Funciones puras para poder probarlas sin Firestore.
import { CLAIM_PHASES, FINAL_STEPS } from '../config/claimFlow';
import { claimStepLabel, getStep } from '../services/flowService';
import { toMillis } from '../config/flowRules';
import { rdAlerts } from './recoverableDepreciation';
import { awaitingPaItems, deadlineItems, daysInStep } from './claimAttention';
import { summarizeChecks } from './companyOverview';

const isActive = record => !FINAL_STEPS.includes(record.flow?.stepId);

export const adjusterHomeSummary = ({ claimRecords = [], checks = [], language = 'es', now = new Date() } = {}) => {
  const active = claimRecords.filter(isActive);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const newThisMonth = claimRecords.filter(record => (toMillis(record.createdAt) || 0) >= monthStart).length;

  const awaiting = awaitingPaItems(claimRecords, checks, language);
  const deadlines = deadlineItems(claimRecords, checks, language);
  const rd = rdAlerts(claimRecords, checks);
  const nowMs = now.getTime();

  // Una sola lista de pendientes: primero lo vencido, luego decisiones, plazos
  // por vencer y depreciación recuperable.
  const attention = [
    ...deadlines.filter(item => item.timer.status === 'overdue').map(item => ({ key: `deadline-${item.key}`, kind: 'overdue', claimRecordId: item.claimRecordId, clientName: item.name, what: item.what })),
    ...awaiting.map(item => ({ key: `decision-${item.key}`, kind: 'decision', claimRecordId: item.claimRecordId, clientName: item.clientName, what: item.what, days: daysInStep(item, nowMs) })),
    ...deadlines.filter(item => item.timer.status !== 'overdue').map(item => ({ key: `deadline-${item.key}`, kind: 'deadline', claimRecordId: item.claimRecordId, clientName: item.name, what: item.what })),
    ...rd.map(item => ({ key: `rd-${item.claimRecordId}`, kind: 'rd', claimRecordId: item.claimRecordId, clientName: item.clientName, amount: item.amount }))
  ];

  const phases = CLAIM_PHASES.map(phase => ({
    id: phase.id,
    label: phase.label,
    count: active.filter(record => getStep('claim', record.flow?.stepId)?.phase === phase.id).length
  }));

  // Los que más tiempo llevan en el mismo paso, para la vista previa de la lista.
  const longestInStep = active
    .map(record => ({ id: record.id, clientName: record.clientName, claimNumber: record.claimNumber, insurer: record.insuranceCompanyName, step: claimStepLabel(record, language), phase: getStep('claim', record.flow?.stepId)?.phase || null, days: daysInStep(record, nowMs) }))
    .sort((a, b) => (b.days ?? -1) - (a.days ?? -1))
    .slice(0, 5);

  const oldestDecision = awaiting.reduce((max, item) => Math.max(max, daysInStep(item, nowMs) ?? 0), 0);

  return {
    total: claimRecords.length,
    active: active.length,
    newThisMonth,
    awaiting: awaiting.length,
    oldestDecision,
    deadlines: deadlines.length,
    overdue: deadlines.filter(item => item.timer.status === 'overdue').length,
    attention,
    phases,
    longestInStep,
    money: summarizeChecks(checks)
  };
};
